import Phaser from "phaser";
import { GAME_HEIGHT, GAME_WIDTH } from "../config/game";
import type { Preferences } from "./storage";
import { TiltController } from "./tilt";

/** Normalizes every gameplay input source into velocity and action intents. */
export class GameInput {
  private readonly cursors: Phaser.Types.Input.Keyboard.CursorKeys;
  private readonly keyA: Phaser.Input.Keyboard.Key;
  private readonly keyD: Phaser.Input.Keyboard.Key;
  private readonly keySpace: Phaser.Input.Keyboard.Key;
  private readonly tilt = new TiltController();
  private pointerTarget: number | null = null;
  private preferences: Preferences;

  constructor(
    private readonly scene: Phaser.Scene,
    preferences: Preferences,
  ) {
    this.preferences = preferences;
    const keyboard = scene.input.keyboard;
    if (!keyboard) throw new Error("Keyboard input unavailable");
    this.cursors = keyboard.createCursorKeys();
    this.keyA = keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.A);
    this.keyD = keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.D);
    this.keySpace = keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.SPACE);
    scene.input.on("pointerdown", this.onPointer, this);
    scene.input.on("pointermove", this.onPointer, this);
    scene.input.on("pointerup", this.onPointerUp, this);
    if (preferences.tiltEnabled) this.tilt.listen(preferences.tiltCenter);
  }

  velocity(playerX: number, speed: number): number {
    const left = this.cursors.left.isDown || this.keyA.isDown;
    const right = this.cursors.right.isDown || this.keyD.isDown;
    const tiltAxis = this.tilt.axis(this.preferences.tiltSensitivity);
    let velocity = (Number(right) - Number(left)) * speed;
    if (velocity === 0 && tiltAxis !== 0) velocity = tiltAxis * speed;

    if (this.pointerTarget !== null) {
      const difference = this.pointerTarget - playerX;
      velocity = Phaser.Math.Clamp(
        difference * 9 * this.preferences.sensitivity,
        -speed * 1.25,
        speed * 1.25,
      );
      if (Math.abs(difference) < 3) velocity = 0;
    }
    return velocity;
  }

  specialPressed(): boolean {
    return Phaser.Input.Keyboard.JustDown(this.keySpace);
  }

  setPreferences(preferences: Preferences): void {
    this.preferences = preferences;
    if (preferences.tiltEnabled) this.tilt.listen(preferences.tiltCenter);
    else this.tilt.disable();
  }

  destroy(): void {
    this.scene.input.off("pointerdown", this.onPointer, this);
    this.scene.input.off("pointermove", this.onPointer, this);
    this.scene.input.off("pointerup", this.onPointerUp, this);
    this.tilt.disable();
  }

  private readonly onPointer = (pointer: Phaser.Input.Pointer): void => {
    if (pointer.isDown && pointer.y >= GAME_HEIGHT * 0.5)
      this.pointerTarget = Phaser.Math.Clamp(pointer.x, 52, GAME_WIDTH - 52);
  };

  private readonly onPointerUp = (): void => {
    this.pointerTarget = null;
  };
}
